import { GridColDef, GridColumnVisibilityModel } from '@mui/x-data-grid';
import { Tooltip, Typography } from '@mui/material';
import { ConnectedDevice, calculateChildProgress } from '@synergetx/puppet-interview-types';
import { format } from 'date-fns/format';
import WifiOutlinedIcon from '@mui/icons-material/WifiOutlined';
import ResultProgress from '@components/ResultProgress';
import TableDate from '@components/TableDate';
import CopyableCell from '@components/CopyableCell';

/**
 * Alignment follows the usual data-table rules: identifiers and prose left, so they share a scan
 * edge; measures right, so magnitudes line up digit-for-digit; status icons and actions centred in
 * a narrow fixed column. Headers match their column, or they end up sitting over empty space.
 */

export const DEVICES_COLUMNS: GridColDef<ConnectedDevice>[] = [
    {
        field: 'deviceName',
        headerName: 'Device',
        // Was `flex: 1`, which handed every spare pixel to the column that needed it least:
        // ~1100px of runway for "iPhone", while progress and assessor were squeezed.
        flex: 0.7,
        minWidth: 150,
        valueGetter: (_value, row) => row?.info?.deviceName ?? '',
        renderCell: ({ value }) => <Typography variant="body2">{value}</Typography>,
    },
    {
        field: 'connections',
        headerName: 'Online',
        width: 90,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (_value, row) =>
            row.connections && Object.keys(row.connections).length ? 1 : 0,
        renderCell: ({ value }) => {
            const online = value === 1;

            // A bare coloured icon is unreadable to anyone who cannot separate red from green.
            return (
                <Tooltip title={online ? 'Online' : 'Offline'}>
                    <WifiOutlinedIcon
                        aria-label={online ? 'Online' : 'Offline'}
                        sx={{ color: online ? 'success.main' : 'text.disabled' }}
                    />
                </Tooltip>
            );
        },
    },
    {
        field: 'lastOnline',
        headerName: 'Last Online',
        width: 150,
        filterable: false,
        valueGetter: (_value, row) => row.lastOnline,
        renderCell: ({ value }) => <TableDate value={value} empty="Currently online" />,
    },
    {
        field: 'interviewer',
        headerName: 'Assessor',
        flex: 1,
        minWidth: 170,
        // Lifted out of the progress cell, where an email was wrapping under a 15px bar in 150px.
        valueGetter: (_value, row) => row?.progress?.result?.interviewer ?? '',
        renderCell: ({ value }) => (
            <Typography variant="body2" noWrap title={value || undefined}>
                {value || '—'}
            </Typography>
        ),
    },
    {
        field: 'progress',
        headerName: 'Survey Progress',
        width: 190,
        filterable: false,
        // Sortable now: "which devices are mid-interview?" is the question this column exists for.
        valueGetter: (_value, row) =>
            row?.progress?.result ? calculateChildProgress(row.progress.result) : -1,
        renderCell: ({ row }) => <ResultProgress result={row?.progress?.result ?? undefined} />,
    },
    {
        field: 'latestAppVersion',
        headerName: 'Latest App Version',
        width: 150,
        filterable: false,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_value, row) => row?.progress?.result?.latestAppVersion ?? '',
        renderCell: ({ value }) => <Typography variant="body2">{value}</Typography>,
    },
    {
        field: 'installationId',
        headerName: 'Installation ID',
        width: 200,
        valueGetter: (_value, row) => row?.info?.installationId ?? '',
        renderCell: ({ value }) => <CopyableCell value={value} label="Installation ID" />,
    },
];

export const DEVICES_COLUMN_VISIBILITY: GridColumnVisibilityModel = {
    latestAppVersion: false,
    installationId: false,
};

/** All columns to include in CSV export */
export const DEVICES_EXPORT_COLUMNS = DEVICES_COLUMNS.map((col) => col.field);

/** Custom formatters for CSV export to handle nested data and transformations */
export const DEVICES_EXPORT_FORMATTERS: Record<
    string,
    (value: unknown, row: ConnectedDevice) => string
> = {
    deviceName: (_value, row) => row?.info?.deviceName ?? '',
    connections: (_value, row) =>
        row.connections && Object.keys(row.connections).length ? 'Online' : 'Offline',
    lastOnline: (_value, row) =>
        row.lastOnline ? format(row.lastOnline, 'Pp') : 'Currently Online',
    progress: (_value, row) => {
        const result = row?.progress?.result;
        if (!result) return '';
        const completed = result.answers?.filter((a) => a.response != null).length ?? 0;
        const total = result.answers?.length ?? 0;
        return total > 0 ? `${Math.round((completed / total) * 100)}%` : '';
    },
    interviewer: (_value, row) => row?.progress?.result?.interviewer ?? '',
    latestAppVersion: (_value, row) => row?.progress?.result?.latestAppVersion ?? '',
    installationId: (_value, row) => row?.info?.installationId ?? '',
};
